import type { HsTransport, HsTransportQueryValue, HsTransportRequest, HsTransportResponse } from '@sibe/hyperscene';

export interface HsHttpTransportOptions {
  /** API origin, for example `https://api.demodev.hyperscene.tech`; request paths are appended to it. */
  baseUrl: string;
  /**
   * Extra headers for every request, typically `Authorization`. Called per request, so a host can refresh
   * tokens without recreating the transport.
   */
  headers?: () => Record<string, string> | Promise<Record<string, string>>;
  /** Fetch implementation; defaults to the global `fetch` at request time. */
  fetch?: typeof fetch;
  /** Aborts a request that has not produced response headers after this many milliseconds. */
  timeoutMs?: number;
  credentials?: RequestCredentials;
}

/** Talks HTTP to the Hyperscene API with `fetch`. Hosts keep authentication in `headers`. */
export class HsHttpTransport implements HsTransport {
  public readonly id: string = 'http';
  private readonly baseUrl: string;

  public constructor(private readonly options: HsHttpTransportOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
  }

  public async request(request: HsTransportRequest): Promise<HsTransportResponse> {
    const headers: Headers = new Headers(request.headers);
    if (request.accept !== undefined && request.accept.length > 0) {
      headers.set('Accept', request.accept.join(', '));
    }
    const body: BodyInit | undefined = this.serializeBody(request.body, headers);
    for (const [name, value] of Object.entries((await this.options.headers?.()) ?? {})) {
      headers.set(name, value);
    }

    const timeout: { signal: AbortSignal | undefined; clear: () => void } = this.makeSignal(request.signal);
    try {
      const response: Response = await (this.options.fetch ?? globalThis.fetch)(this.makeUrl(request), {
        method: request.method ?? 'GET',
        headers,
        body,
        signal: timeout.signal,
        credentials: this.options.credentials,
      });
      return wrapResponse(response);
    } finally {
      timeout.clear();
    }
  }

  private makeUrl(request: HsTransportRequest): string {
    const path: string = request.path.replace(/^\/+/, '');
    const query: string = makeQuery(request.query);
    return `${this.baseUrl}/${path}${query.length > 0 ? `?${query}` : ''}`;
  }

  private serializeBody(body: unknown, headers: Headers): BodyInit | undefined {
    if (body === undefined) {
      return undefined;
    }
    if (
      typeof body === 'string' ||
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body) ||
      (typeof Blob !== 'undefined' && body instanceof Blob) ||
      (typeof FormData !== 'undefined' && body instanceof FormData)
    ) {
      return body as BodyInit;
    }
    headers.set('Content-Type', 'application/json');
    return JSON.stringify(body);
  }

  private makeSignal(external: AbortSignal | undefined): { signal: AbortSignal | undefined; clear: () => void } {
    if (this.options.timeoutMs === undefined) {
      return { signal: external, clear: () => undefined };
    }
    const controller: AbortController = new AbortController();
    const abort = (): void => controller.abort(external?.reason);
    if (external?.aborted === true) {
      abort();
    }
    external?.addEventListener('abort', abort, { once: true });
    const timer: ReturnType<typeof setTimeout> = setTimeout(
      () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
      this.options.timeoutMs
    );
    return {
      signal: controller.signal,
      clear: () => {
        clearTimeout(timer);
        external?.removeEventListener('abort', abort);
      },
    };
  }
}

function makeQuery(query: Readonly<Record<string, HsTransportQueryValue>> | undefined): string {
  const params: URLSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item: string) => params.append(key, item));
    } else {
      params.append(key, String(value));
    }
  }
  return params.toString();
}

function wrapResponse(response: Response): HsTransportResponse {
  const headers: Record<string, string> = {};
  response.headers.forEach((value: string, name: string) => {
    headers[name.toLowerCase()] = value;
  });
  return {
    status: response.status,
    statusText: response.statusText,
    contentType: (response.headers.get('Content-Type') ?? '').split(';')[0].trim().toLowerCase(),
    headers,
    arrayBuffer: () => response.arrayBuffer(),
    blob: () => response.blob(),
    text: () => response.text(),
    json: <T>() => response.json() as Promise<T>,
  };
}
